import { readAuthFlow, clearAuthFlow } from './authFlowContext';

// Provider descriptions can contain addresses or credentials. Only bounded
// codes and categories may leave the browser in analytics.
const ERROR_CODES = new Set([
  'email_exists', 'user_already_exists', 'identity_already_exists', 'identity_already_linked',
  'over_email_send_rate_limit', 'over_request_rate_limit', 'otp_expired', 'flow_state_expired',
  'guest_transfer_expired', 'guest_session_expired', 'weak_password', 'same_password',
  'session_not_found', 'user_not_found', 'refresh_token_not_found', 'refresh_token_already_used',
  'bad_jwt', 'invalid_credentials', 'email_not_confirmed', 'email_address_invalid',
  'email_address_not_authorized', 'unexpected_failure', 'invalid_client',
  'server_error', 'access_denied', 'request_timeout', 'oauth_callback_error',
  'bad_oauth_callback', 'bad_oauth_state', 'bad_code_verifier', 'flow_state_not_found',
  'flow_state_already_used', 'oauth_client_state_not_found', 'oauth_client_state_expired',
  'oauth_invalid_state', 'provider_disabled', 'oauth_provider_not_supported',
  'provider_email_needs_verification', 'session_expired',
]);

export function authErrorCode(error) {
  return ERROR_CODES.has(error?.code) ? error.code : 'unknown';
}

export function authErrorType(error) {
  const code = authErrorCode(error);
  if (['email_exists', 'user_already_exists', 'identity_already_exists', 'identity_already_linked'].includes(code)) return 'account_exists';
  if (['over_email_send_rate_limit', 'over_request_rate_limit'].includes(code) || error?.status === 429) return 'rate_limit';
  if (['otp_expired', 'flow_state_expired', 'flow_state_not_found', 'flow_state_already_used',
    'oauth_client_state_not_found', 'oauth_client_state_expired', 'guest_transfer_expired', 'guest_session_expired'].includes(code)) return 'expired';
  if (['weak_password', 'same_password'].includes(code)) return 'password';
  if (['session_not_found', 'session_expired', 'user_not_found', 'refresh_token_not_found', 'refresh_token_already_used', 'bad_jwt',
    'bad_oauth_callback', 'bad_oauth_state', 'bad_code_verifier', 'oauth_invalid_state'].includes(code)) return 'auth';
  if (['email_address_invalid', 'email_address_not_authorized'].includes(code)) return 'email';
  if (code === 'unexpected_failure' && /(?:send|sending|deliver).*email|email.*(?:send|deliver)/i.test(error?.message || '')) return 'email_delivery';
  if (['invalid_client','provider_disabled','oauth_provider_not_supported'].includes(code)) return 'provider_config';
  if (code === 'access_denied') return 'cancelled';
  if (error?.name === 'AbortError' || code === 'request_timeout' || /timed?\s*out|too long/i.test(error?.message || '')) return 'timeout';
  if (/failed to fetch|network/i.test(error?.message || '')) return 'network';
  return 'application';
}

export function authErrorParams(error, stage) {
  return { error_type: authErrorType(error), error_code: authErrorCode(error), guest_stage: stage };
}

// Capture before Supabase consumes the OAuth fragment; sanitize before any
// page-view tracking. Successful token and recovery callbacks stay untouched.
export function captureAuthCallbackError(browser = window) {
  const url = new URL(browser.location.href);
  const fragment = new URLSearchParams(url.hash.slice(1));
  const params = fragment.has('error') || fragment.has('error_code') ? fragment : url.searchParams;
  if (!params.has('error') && !params.has('error_code')) return null;
  const description = params.get('error_description') || '';
  const code = /(?:^|[^a-z_])invalid_client(?:[^a-z_]|$)/i.test(description)
    ? 'invalid_client' : authErrorCode({ code: params.get('error_code') || params.get('error') });
  const context = readAuthFlow(browser);
  const method = ['google', 'apple', 'email'].includes(url.searchParams.get('auth_method'))
    ? url.searchParams.get('auth_method') : url.searchParams.has('finish_guest_email') ? 'email' : context?.method || 'unknown';
  const target = ['signup','login'].includes(url.searchParams.get('auth_target'))
    ? url.searchParams.get('auth_target') : context?.target || 'login';
  ['error', 'error_code', 'error_description', 'error_uri', 'code', 'access_token', 'refresh_token',
    'id_token', 'provider_token', 'provider_refresh_token'].forEach(key => url.searchParams.delete(key));
  url.hash = '';
  browser.history.replaceState(browser.history.state, '', `${url.pathname}${url.search}`);
  clearAuthFlow(browser);
  return { code: code === 'unknown' ? 'oauth_callback_error' : code, method, target };
}
